/**
 * Normalize the dashboard payload's `keyword_details` field into an ordered
 * list of `{ key, label, raw }` items, regardless of the wire format:
 *
 * - Object map: `{ "computer science": { top_institution: [...], ... }, ... }`
 *   (current shape; keys are normalized topic strings).
 * - Legacy array: `[{ keyword: "computer science", top_institution: [...] }]`
 *   (older shape; kept working for safety).
 *
 * Iteration order follows `selectedKeywords` so chips and ranking tabs share
 * the user's chosen ordering. Keywords not yet returned by the API are
 * preserved in the result with `raw = null`, letting the UI render an empty
 * tab instead of dropping the chip silently.
 */

export const normalizeKeyword = (value) =>
  String(value || "").trim().toLowerCase();

export const formatKeywordLabel = (value) =>
  String(value || "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const buildLookup = (keywordDetails) => {
  const map = new Map();
  if (!keywordDetails) return map;

  if (Array.isArray(keywordDetails)) {
    keywordDetails.forEach((entry) => {
      const key = normalizeKeyword(entry?.keyword);
      if (key) map.set(key, entry);
    });
    return map;
  }

  if (typeof keywordDetails === "object") {
    Object.entries(keywordDetails).forEach(([keyword, raw]) => {
      const key = normalizeKeyword(keyword);
      if (key) map.set(key, raw);
    });
  }
  return map;
};

const collectFallbackKeywords = (keywordDetails) => {
  if (!keywordDetails) return [];
  if (Array.isArray(keywordDetails)) {
    return keywordDetails.map((entry) => entry?.keyword).filter(Boolean);
  }
  if (typeof keywordDetails === "object") {
    return Object.keys(keywordDetails);
  }
  return [];
};

export const buildKeywordDetailItems = ({
  keywordDetails,
  selectedKeywords = [],
  keywordLabelMap = {},
}) => {
  const lookup = buildLookup(keywordDetails);
  const fallback = collectFallbackKeywords(keywordDetails);

  const sourceKeywords = selectedKeywords.length ? selectedKeywords : fallback;
  if (!sourceKeywords.length) return [];

  const seen = new Set();
  const items = [];

  sourceKeywords.forEach((keyword) => {
    const key = normalizeKeyword(keyword);
    if (!key || seen.has(key)) return;
    seen.add(key);
    items.push({
      key,
      label:
        keywordLabelMap[key] ||
        keywordLabelMap[keyword] ||
        formatKeywordLabel(keyword),
      raw: lookup.get(key) || null,
    });
  });

  return items;
};
